import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { getCurrencySymbol } from "../utils/formatters";
import {
  getCurrentCurrencySettings,
  setCurrencyFromCountry,
} from "../utils/countryBasedCurrency";

const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const { user } = useAuth();
  const [companyInfo, setCompanyInfo] = useState(() => {
    // Initialize with country-based currency settings
    const currencySettings = getCurrentCurrencySettings();
    return {
      name: "Account Website",
      logo: null,
      email: "",
      contact: "",
      fullName: "",
      country: currencySettings.country || "",
      // Regional settings from country-based detection
      currency: currencySettings.currency,
      currencySymbol: currencySettings.currencySymbol,
      currencyDisplay: localStorage.getItem("currencyDisplay") || "symbol",
      currencyPosition: localStorage.getItem("currencyPosition") || "before",
      dateFormat: localStorage.getItem("dateFormat") || "DD/MM/YYYY",
      numberFormat: localStorage.getItem("numberFormat") || "comma",
    };
  });
  const [userKey, setUserKey] = useState(0);

  // Enhanced setCompanyInfo to ensure currency symbol is always correct
  const setCompanyInfoWithCurrency = (newInfo) => {
    // If currency is being updated, ensure the symbol is set correctly
    if (
      newInfo.currency &&
      (!newInfo.currencySymbol || newInfo.currency !== companyInfo.currency)
    ) {
      newInfo.currencySymbol = getCurrencySymbol(newInfo.currency);
    }

    // Set the updated company info
    setCompanyInfo((prev) => ({
      ...prev,
      ...newInfo,
    }));
  };

  // Sync company info with user data whenever user changes
  useEffect(() => {
    // Force a new key whenever user changes (including login/logout)
    setUserKey((prev) => prev + 1);

    if (user && user.email) {
      console.log(`👤 User data received:`, {
        email: user.email,
        country: user.country,
        countryCode: user.countryCode,
        currency: user.currency,
        currencySymbol: user.currencySymbol
      });

      // If user has currency data in their profile, use that instead of localStorage
      let userCurrency = null;
      let userCurrencySymbol = null;
      let userCountry = null;
      let userCountryCode = null;

      if (user.currency && user.currencySymbol) {
        userCurrency = user.currency;
        userCurrencySymbol = user.currencySymbol;
        console.log(`🎯 Using user profile currency: ${userCurrency} (${userCurrencySymbol})`);
        
        // Update localStorage to match user profile
        localStorage.setItem("currency", userCurrency);
        localStorage.setItem("currencySymbol", userCurrencySymbol);
      }

      if (user.country) {
        userCountry = user.country;
        if (user.countryCode) {
          userCountryCode = user.countryCode;
        }
        console.log(`🌍 Using user profile country: ${userCountry} (${userCountryCode || 'no code'})`);
        
        // Update localStorage to match user profile
        localStorage.setItem("country", userCountry);
        if (userCountryCode) {
          localStorage.setItem("countryCode", userCountryCode);
        }
      }

      // Get currency settings (will now use the updated localStorage values)
      const currencySettings = getCurrentCurrencySettings();

      setCompanyInfoWithCurrency({
        name: user.company?.name || "Account Website",
        logo: user.company?.logo || null,
        email: user.company?.email || user.email || "",
        contact: user.company?.contact || "",
        fullName: user.fullName || "",
        country: userCountry || currencySettings.country,
        currency: userCurrency || currencySettings.currency,
        currencySymbol: userCurrencySymbol || currencySettings.currencySymbol,
        currencyDisplay:
          user.company?.currencyDisplay ||
          localStorage.getItem("currencyDisplay") ||
          "symbol",
        currencyPosition:
          user.company?.currencyPosition ||
          localStorage.getItem("currencyPosition") ||
          "before",
        dateFormat:
          user.company?.dateFormat ||
          localStorage.getItem("dateFormat") ||
          "DD/MM/YYYY",
        numberFormat:
          user.company?.numberFormat ||
          localStorage.getItem("numberFormat") ||
          "comma",
      });
      
           // Ensure currency settings are persisted after login with proper symbol
           const finalCurrency = userCurrency || currencySettings.currency;
           const finalSymbol = userCurrencySymbol || currencySettings.currencySymbol;
           
           if (finalCurrency && finalSymbol) {
             localStorage.setItem("currency", finalCurrency);
             localStorage.setItem("currencySymbol", finalSymbol);
             console.log(`💾 Currency persisted after login: ${finalCurrency} (${finalSymbol})`);
             
             // Trigger formatter refresh to update all components
             window.dispatchEvent(new CustomEvent("formatters-refresh"));
           }
           
           // Force currency refresh to fix any mismatches
           setTimeout(() => {
             const { forceCurrencyRefresh } = require("../utils/countryBasedCurrency");
             forceCurrencyRefresh();
           }, 100);
    } else if (user === null) {
      // Reset to country-based defaults when user is null (logout)
      const currencySettings = getCurrentCurrencySettings();
      setCompanyInfo({
        name: "Account Website",
        logo: null,
        email: "",
        contact: "",
        fullName: "",
        country: currencySettings.country,
        currency: currencySettings.currency,
        currencySymbol: currencySettings.currencySymbol,
        currencyDisplay: "symbol",
        currencyPosition: "before",
        dateFormat: "DD/MM/YYYY",
        numberFormat: "comma",
      });
    }
  }, [user]);

  // Save regional settings to localStorage on change
  useEffect(() => {
    localStorage.setItem("currency", companyInfo.currency || "USD");
    localStorage.setItem(
      "currencySymbol",
      companyInfo.currencySymbol || getCurrencySymbol(companyInfo.currency)
    );
    localStorage.setItem(
      "currencyDisplay",
      companyInfo.currencyDisplay || "symbol"
    );
    localStorage.setItem(
      "currencyPosition",
      companyInfo.currencyPosition || "before"
    );
    localStorage.setItem("dateFormat", companyInfo.dateFormat || "DD/MM/YYYY");
    localStorage.setItem("numberFormat", companyInfo.numberFormat || "comma");

    // Save country info for currency detection
    if (companyInfo.country) {
      localStorage.setItem("country", companyInfo.country);
    }
  }, [companyInfo]);

  return (
    <CompanyContext.Provider
      key={userKey}
      value={{ companyInfo, setCompanyInfo: setCompanyInfoWithCurrency }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error("useCompany must be used within a CompanyProvider");
  }
  return context;
};
